<template>
    <div :class="chartWrapperClass">
        <slot name="above" />
        <highcharts
            class="chart-container"
            :class="chartClass"
            :style="graphStyle"
            :callback="onRender"
            :options="finalChartOptions"
            :deep-copy-on-update="false" />
        <chart-legend
            ref="chartLegend"
            :is-aggregate="isAggregate"
            :series="isAggregate ? series[0].data : series"
            :rendered-series="Chart.series"
            :chart="Chart"
            :only-total-active-by-default="onlyTotalActiveByDefault" />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapState } from 'vuex';
import merge from 'lodash/merge';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { EventBus } from '@utils/event-bus';
import HIGHCHARTS_BASE_CHART from './chart-config.js';
import ChartLegend from './legend/chart-legend.vue';

export default {
    name: 'ChartBase',
    components: {
        ChartLegend,
    },
    props: {
        autoHeight: {
            type: Boolean,
            default: false,
        },
        chartOptions: {
            type: Object,
            default: () => {},
        },
        accessibilityDescription: {
            type: String,
            default: '',
        },
        onlyTotalActiveByDefault: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['onRender'],
    data () {
        const baseChart = cloneDeep(HIGHCHARTS_BASE_CHART);

        return {
            finalChartOptions: {},
            baseChart,
            Chart: {},
            legendHeight: 0,
        };
    },
    computed: {
        ...mapState({
            subscription: (state) => state.subscription.active,
        }),
        chartWrapperClass () {
            return {
                'chart-wrapper': true,
                'flex-chart-wrapper': this.autoHeight,
            };
        },
        chartClass () {
            return {
                'chart': !this.autoHeight,
                'pie-chart': this.chartType === 'pie',
            };
        },
        isAggregate () {
            return this.series.length === 1 && this.series[0].isAggregateChart;
        },
        series () {
            return get(this.finalChartOptions, 'series') || [];
        },
        graphStyle () {
            if (!this.autoHeight) return {};

            return {
                height: `calc(100% - ${this.legendHeight}px)`,
                minHeight: '70%',
            };
        },
        chartType () {
            return this.series[0]?.type;
        },
    },
    watch: {
        chartOptions () {
            this.buildChart();
        },
    },
    created () {
        this.setTimeZone();
        this.buildChart();
        EventBus.$on('reflowChart', this.reflowChart);
    },
    beforeUnmount () {
        EventBus.$off('reflowChart', this.reflowChart);

        if (this.Chart?.destroy) {
            this.Chart.destroy();
            this.Chart = null;
        }
    },
    methods: {
        buildChart () {
            const base = cloneDeep(this.baseChart);
            const options = merge(base, this.chartOptions);

            if (this.accessibilityDescription) {
                options.accessibility = merge(options.accessibility || {}, {
                    description: this.accessibilityDescription,
                });
                options.lang = merge(options.lang || {}, {
                    accessibility: {
                        svgContainerTitle: this.accessibilityDescription,
                        svgContainerLabel: this.accessibilityDescription,
                        chartContainerLabel: this.accessibilityDescription,
                    },
                });
            }
            this.finalChartOptions = options;

            if (this.autoHeight) {
                this.$nextTick(() => {
                    this.legendHeight = this.$refs.chartLegend?.$el.clientHeight || 0;
                    this.reflowChart();
                });
            }
        },
        setTimeZone () {
            const timezone = this.subscription.timeZone;
            this.baseChart = Object.assign(this.baseChart, {
                time: { timezone },
            });
        },
        onRender (chart) {
            this.Chart = markRaw(chart);
            this.$emit('onRender', chart);
        },
        reflowChart () {
            if (this.Chart.reflow) {
                this.Chart.reflow();
            }
        },
    },
};
</script>

<style lang="less" scoped>
.flex-chart-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
}

.chart-container {
    width: 100%;
    height: 100%;
    overflow: auto;
}
</style>
